import { produce } from 'immer';
import { SubflowMessengerAction } from 'lib-shared-types-api';
import groupBy from 'lodash/groupBy';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import {
  DixaMessengerSubflow,
  UseFlowValue,
} from 'components/settings/components/sections/dixaMessenger/views/flow/hooks';

type Args = {
  subflow: DixaMessengerSubflow;
  mainFlow: UseFlowValue['localCopyOfFlow'];
  highlightedNodes: SubflowMessengerAction[];
};
export function copyHighlightedNodesFromMainFlowIntoSubflow({
  subflow,
  mainFlow,
  highlightedNodes,
}: Args): DixaMessengerSubflow {
  // New root node id for the subflow, since this is making a copy of the highlighted nodes form the main flow, the root node id must be unique
  const newRootNodeId = uuid();

  return produce(subflow, (draftSubflow) => {
    const draftSubflowInitNode = draftSubflow.actions.find(
      (node) => node.nodeType === FlowNodeTypes.INIT,
    );

    if (!draftSubflowInitNode) {
      return draftSubflow;
    }

    const highlightedNodesRootNode = highlightedNodes.find(
      ({ parentId }) =>
        parentId === null ||
        !highlightedNodes.find(
          (highlightedNode) => highlightedNode.id === parentId,
        ),
    );

    if (!highlightedNodesRootNode) {
      return subflow;
    }

    const highlightedNodesExceptRootNode = highlightedNodes.filter(
      (node) => node.id !== highlightedNodesRootNode.id,
    );

    type NodeGroup = 'directChildOfRoot' | 'childOfChild';

    // Group the highlighted nodes (except the root node) into direct children of the root node and children of children
    const { directChildOfRoot, childOfChild } = groupBy(
      highlightedNodesExceptRootNode,
      (node: SubflowMessengerAction): NodeGroup =>
        node.parentId === highlightedNodesRootNode.id
          ? 'directChildOfRoot'
          : 'childOfChild',
    ) as Record<NodeGroup, SubflowMessengerAction[]>;

    // Modify the root node to be the new root node
    const updatedRootNode = produce(
      highlightedNodesRootNode,
      (draftHighlightedRootNode) => {
        // the first highlighted node's parent id must refer to the init sub flow node
        draftHighlightedRootNode.parentId = draftSubflowInitNode.id;
        // the uuid is also updated to prevent conflicts
        // as it was reused by the subflowreference node
        draftHighlightedRootNode.id = newRootNodeId;
      },
    );

    const originalParentOfRootNode = mainFlow?.actions.find(
      (action) => action.id === highlightedNodesRootNode.parentId,
    );
    /**
     * Set the init sub flow node to the same position as the parent for the
     * first highlighted node
     */
    draftSubflowInitNode.pageX = originalParentOfRootNode?.pageX ?? 0;
    draftSubflowInitNode.pageY = originalParentOfRootNode?.pageY ?? 0;

    // add the updated modified root node, with updated parent id and updated coordinates
    draftSubflow.actions.push(updatedRootNode as SubflowMessengerAction);

    // point the parent id of the direct children of the root node to the new root node
    const rootNodeChildrenWithUpdatedParentId = directChildOfRoot.map(
      (node) => ({ ...node, parentId: newRootNodeId }),
    );

    // Add the direct root children with the new root node id as parent id
    draftSubflow.actions.push(...rootNodeChildrenWithUpdatedParentId);

    // Add the children of the children without changing the parent id
    draftSubflow.actions.push(...childOfChild);
  });
}
